import { useEffect, useState } from 'react';
import { Check, Loader2, Circle, X } from 'lucide-react';
import type { ExportJob, ExportPhase } from '@atlas/shared';

const PHASES: Array<{ key: ExportPhase; label: string }> = [
  { key: 'discovering', label: 'Discovering tables' },
  { key: 'exporting_tables', label: 'Exporting tables' },
  { key: 'validating', label: 'Validating isolation' },
  { key: 'transferring_attachments', label: 'Transferring files' },
  { key: 'provisioning_access', label: 'Provisioning SFTP access' },
];

type StepState = 'done' | 'active' | 'failed' | 'pending';

/**
 * How many phases have fully completed, inferred from the artifacts on the
 * document (so it's accurate even for failed exports, where `phase` is null).
 */
function completedCount(exp: ExportJob): number {
  const { tableResults, validation, attachments, sftp, status } = exp;
  let n = 0;
  if (tableResults.length > 0 || validation || attachments || sftp) n = 1; // discovered
  if (validation || attachments || sftp) n = 2; // exported
  if (
    (validation && validation.isolationViolations === 0 && validation.countsReconciled) ||
    attachments ||
    sftp
  )
    n = 3; // validated
  if (attachments || sftp) n = 4; // attachments transferred
  if (sftp || status === 'complete') n = 5; // provisioned
  return n;
}

function stepStates(exp: ExportJob): StepState[] {
  if (exp.status === 'complete') return PHASES.map(() => 'done');

  if (exp.status === 'running') {
    const activeIdx = exp.phase
      ? PHASES.findIndex((p) => p.key === exp.phase)
      : 0;
    return PHASES.map((_, i) =>
      i < activeIdx ? 'done' : i === activeIdx ? 'active' : 'pending',
    );
  }

  // failed / cancelled — the step after the last completed one is where it died.
  const done = completedCount(exp);
  const failedIdx = Math.min(done, PHASES.length - 1);
  return PHASES.map((_, i) =>
    i < done ? 'done' : i === failedIdx ? 'failed' : 'pending',
  );
}

function stepDetail(exp: ExportJob, key: ExportPhase): string | null {
  switch (key) {
    case 'exporting_tables': {
      if (exp.tableResults.length === 0) return null;
      const rows = exp.tableResults.reduce((s, t) => s + t.exportedRowCount, 0);
      return `${exp.tableResults.length} tables · ${rows.toLocaleString()} rows`;
    }
    case 'validating':
      if (!exp.validation) return null;
      return exp.validation.isolationViolations > 0
        ? `${exp.validation.isolationViolations} isolation violation(s)`
        : `${exp.validation.totalRows.toLocaleString()} rows · isolation clean`;
    case 'transferring_attachments':
      if (!exp.attachments) return null;
      return `${exp.attachments.transferred}/${exp.attachments.totalFiles} files`;
    case 'provisioning_access':
      return exp.sftp ? `user ${exp.sftp.username}` : null;
    default:
      return null;
  }
}

function StepIcon({ state }: { state: StepState }) {
  if (state === 'done') return <Check size={14} className="text-[#0f7b6c]" />;
  if (state === 'active')
    return <Loader2 size={14} className="text-[#2383e2] animate-spin" />;
  if (state === 'failed') return <X size={14} className="text-[#e03e3e]" />;
  return <Circle size={14} className="text-[#d3d1cb]" />;
}

function timeAgo(iso: string, now: number): string {
  const secs = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ${secs % 60}s ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m ago`;
}

/** Live "updated Xs ago" that ticks every second while the export is running. */
function Heartbeat({ exp }: { exp: ExportJob }) {
  const [now, setNow] = useState(() => Date.now());
  const running = exp.status === 'running' || exp.status === 'queued';

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [running]);

  const stale = running && now - new Date(exp.updatedAt).getTime() > 90_000;

  return (
    <div className="flex items-center gap-1.5 text-[11px]">
      {running && (
        <span
          className={`w-1.5 h-1.5 rounded-full ${
            stale ? 'bg-[#d9730d]' : 'bg-[#2383e2] animate-pulse'
          }`}
        />
      )}
      <span className={stale ? 'text-[#d9730d]' : 'text-[#aeaaa4]'}>
        {exp.status === 'queued'
          ? 'Queued — waiting for worker…'
          : running
            ? `heartbeat · updated ${timeAgo(exp.updatedAt, now)}`
            : `updated ${timeAgo(exp.updatedAt, now)}`}
        {stale && ' (no recent progress)'}
      </span>
    </div>
  );
}

export function ExportProgress({ exp }: { exp: ExportJob }) {
  const states = stepStates(exp);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-[#37352f]">Progress</span>
        <Heartbeat exp={exp} />
      </div>

      <ol className="flex flex-col">
        {PHASES.map((p, i) => {
          const state = states[i]!;
          const detail = stepDetail(exp, p.key);
          const isLast = i === PHASES.length - 1;
          return (
            <li key={p.key} className="flex gap-2.5">
              {/* Icon + connector rail */}
              <div className="flex flex-col items-center">
                <span className="flex items-center justify-center w-5 h-5 shrink-0">
                  <StepIcon state={state} />
                </span>
                {!isLast && (
                  <span
                    className={`w-px flex-1 min-h-[14px] ${
                      state === 'done' ? 'bg-[#0f7b6c]' : 'bg-[#e9e9e7]'
                    }`}
                  />
                )}
              </div>
              {/* Label + detail */}
              <div className={`pb-2 ${isLast ? '' : ''}`}>
                <div
                  className={`text-xs ${
                    state === 'pending'
                      ? 'text-[#aeaaa4]'
                      : state === 'failed'
                        ? 'text-[#e03e3e] font-medium'
                        : state === 'active'
                          ? 'text-[#2383e2] font-medium'
                          : 'text-[#37352f]'
                  }`}
                >
                  {p.label}
                </div>
                {detail && (
                  <div className="text-[11px] text-[#aeaaa4] mt-0.5">{detail}</div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
